/**
 * Who can open what in the CRM.
 *
 * Every gated feature used to re-derive its own `user?.role === "admin"` check
 * inline, which meant widening access was a sweep of ~40 files. It is now one
 * place: `hasAdminAccess`.
 *
 * The dev role (the internal media / tech team) passes every admin gate on
 * purpose. Dev accounts exist so the tech team can build and test the CRM, and
 * they cannot do that from behind an admin-only lock. This grants *feature*
 * access only. A dev is still not a sales role, so they stay out of the
 * leaderboard, the commissions roster and the "My Stats" agent switcher. Those
 * lists filter on `role !== 'dev'` and must keep doing so.
 *
 * Mirrors the backend: `core.permissions.has_admin_access` /
 * `CustomUser.has_admin_access`. Keep the two in step.
 */

const roleList = (user) => {
  if (!user) return [];
  const secondary = Array.isArray(user.secondary_roles) ? user.secondary_roles : [];
  return [user.role, ...secondary].filter(Boolean).map((r) => String(r).toLowerCase());
};

/** True if `user` holds `role` as either their primary or a secondary role. */
export const hasRole = (user, role) => roleList(user).includes(String(role).toLowerCase());

/**
 * True only when `role` is what this account primarily IS, ignoring second jobs.
 *
 * Needed since an agent can also assist a colleague (Settings > My Assistant):
 * that adds 'pa' to `secondary_roles`, and the agency-wide scope must not follow
 * it. Mirrors the primary-role check in `core.permissions.can_view_agency_wide`.
 */
export const hasPrimaryRole = (user, role) =>
  Boolean(user) && String(user.role || "").toLowerCase() === String(role).toLowerCase();

/**
 * Human-readable name for a role code, for anywhere we show a role to a user.
 * Avoids the raw "super_admin" (which a `capitalize` class turns into the ugly
 * "Super_admin"). Unknown codes fall back to spaced, title-cased text.
 */
const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  dev: "Media / Tech",
  agent: "Agent",
  pa: "Personal Assistant",
  buyer: "Buyer",
  vendor: "Vendor",
};

export const roleLabel = (role) => {
  const key = String(role || "").toLowerCase();
  if (ROLE_LABELS[key]) return ROLE_LABELS[key];
  return key
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

/** The internal media / tech team. */
export const isDevUser = (user) => hasRole(user, "dev");

/** The agency principal. A super admin is an admin PLUS the agency-level half. */
export const isSuperAdminUser = (user) => hasRole(user, "super_admin");

/** A real sales agent (a dev is deliberately NOT one of these). */
export const isAgentUser = (user) => hasRole(user, "agent");

/**
 * Agent-level CRM access: sales agents plus devs.
 *
 * NOT the mirror of the backend's `core.permissions.has_agent_access`, which
 * also counts PAs (7 Sep 2026 — "being a PA shouldnt change any permissions that
 * an agent has"). The two differ on purpose, because on this side the value is
 * mostly used as `hasAgentAccess(user) && !hasAdminAccess(user)`, i.e. "show the
 * cut-down AGENT view of a page". A PA already gets the admin sidebar and the
 * agency-wide default scope, so adding them here would TAKE tabs away from them
 * — the ten admin-only Portal Analytics tabs among others — which is the
 * opposite of what was asked.
 *
 * If you want "may this person use the agent side of the CRM at all", that is
 * `isCrmStaff`.
 */
export const hasAgentAccess = (user) => isAgentUser(user) || isDevUser(user);

/**
 * Admin-level CRM access: may open every gated feature, admin-only action and
 * agency-wide data scope. Admins, Django staff/superusers and devs.
 */
export const hasAdminAccess = (user) =>
  Boolean(
    user &&
      (hasRole(user, "admin") ||
        isSuperAdminUser(user) ||
        user.is_superuser === true ||
        user.is_staff === true ||
        isDevUser(user))
  );

/**
 * Agency-level CRM access — a STRICT SUBSET of `hasAdminAccess`.
 *
 * Two things live up here and nothing else:
 *   1. The CRM Analytics and Executive analytics environments. Christian,
 *      31 Aug 2026: "Cut CRM analytics & Executive from other admins".
 *   2. The DEFAULT agency-wide data scope — the pipeline, the dashboard tiles
 *      and the Sent Contracts panel showing every agent's rows rather than your
 *      own. Simon Abbott, same day: "can we please completely separate my CRM
 *      from other agents? I think because I'm admin I'm getting other agents
 *      appraisals in my pipeline."
 *
 * A plain admin keeps every admin-only PAGE they had (Agent Management, the
 * Settings admin panel, the trash pages, Commissions, eSign's agency view) and
 * can still switch a scoped surface to "All agents" by hand. What changed is
 * only what they see by DEFAULT, and the two analytics environments.
 *
 * ROLE ONLY. Unlike `hasAdminAccess` this deliberately ignores `is_staff` and
 * `is_superuser`: every admin account in this system is created as a Django
 * superuser, so those flags identify the whole admin group rather than the
 * principal, and honouring them here would undo the split entirely.
 *
 * Mirrors the backend `core.permissions.has_super_admin_access`. Keep in step.
 */
export const hasSuperAdminAccess = (user) =>
  Boolean(user && (isSuperAdminUser(user) || isDevUser(user)));

/** Anyone who works inside the agency (blocks buyer / vendor portal accounts). */
export const isCrmStaff = (user) =>
  hasAdminAccess(user) || hasAgentAccess(user) || hasRole(user, "pa");

/**
 * Does this user see agency-wide rows by default on a scoped surface?
 *
 * The agency principal only. A plain admin answers no (that was the fix for
 * Simon's pipeline), and as of 7 Sep 2026 so does a PA — they get the desk they
 * assist on instead. See `canViewAgencyWide`.
 */
export const seesAgencyWideByDefault = (user) => hasSuperAdminAccess(user);

/**
 * May this user switch a scoped surface to the agency-wide view at all?
 *
 * THE AGENCY PRINCIPAL ONLY — super admin (and dev). Christian Gravias, 7 Sep
 * 2026, Monday 12984138077: "Agents can see eachothers appraisals, leads,
 * communications. Please only make it that super admin can see everything and
 * the agents only see there own data."
 *
 * So the All agents / My records switch is no longer rendered for a plain admin
 * or a PA. An admin covering for a colleague uses the "+ Include an agent"
 * picker instead (`canIncludeColleagues`), which is the narrower thing Simon
 * Abbott asked for on 3 Sep — "mine and Gennevas", not the office. A PA reaches
 * the agent they assist through that agent's desk, on the backend.
 *
 * ROLE ONLY, like `hasSuperAdminAccess` and for the same reason: this used to
 * go through `hasAdminAccess`, which honours `is_staff` / `is_superuser`, and at
 * least one real AGENT account carries both flags. That agent was handed the
 * whole office everywhere a surface is scoped - George Bottas, 4 Sep 2026:
 * "Communcations showing the whole offices emails being sent. Please only show
 * my ones or the team I am assigned with."
 *
 * Mirrors the backend `core.permissions.can_view_agency_wide`. Keep in step.
 */
export const canViewAgencyWide = (user) => hasSuperAdminAccess(user);

/**
 * May this user fold a NAMED colleague's rows in beside their own?
 *
 * The "+ Include an agent" picker. The admin tier — one step wider than
 * `canViewAgencyWide`, because covering for one colleague is not the same as
 * reading the whole office, and taking the office view off the admins would
 * otherwise have taken this with it.
 *
 * ROLE ONLY (no `is_staff` / `is_superuser`), for the reason spelled out on
 * `canViewAgencyWide`. Mirrors the backend
 * `core.permissions.can_include_colleagues`. Keep in step.
 */
export const canIncludeColleagues = (user) =>
  Boolean(user && (hasRole(user, "admin") || isSuperAdminUser(user) || isDevUser(user)));

export default hasAdminAccess;
